import React, { useState, useEffect } from 'react';
import {
    AiOutlineShop,
    AiOutlineTeam,
    AiOutlineBank,
    AiOutlineDollar,
    AiOutlineCheckCircle,
    AiOutlineSetting,
    AiOutlineInfoCircle,
} from 'react-icons/ai';
import styles from './BusinessSettings.module.css';
import AxiosUrl from '../AxiosUrl';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import ReactLoading from 'react-loading';

function BusinessSettings() {
    const uid = localStorage.getItem('id');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [settings, setSettings] = useState({
        businessType: 'single',
        accountingMode: 'simple',
        features: {
            enableCashFlow: false,
            enableInventory: false,
            enablePartners: false,
            enableApproval: false,
        },
        currency: 'BDT',
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await AxiosUrl.get(`/business-settings/${uid}`);
            if (response.data.success) {
                setSettings(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        // Validation
        if (settings.businessType === 'partnership' || settings.businessType === 'company') {
            if (!settings.features.enablePartners) {
                toast.warning('Partnership/Company businesses must enable Partner features');
                return;
            }
        }

        if (settings.accountingMode === 'advanced') {
            if (settings.businessType === 'single') {
                toast.warning('Advanced accounting mode requires Partnership or Company business type');
                return;
            }
        }

        setSaving(true);
        try {
            const response = await AxiosUrl.post(`/business-settings/${uid}`, {
                settings,
            });

            if (response.data.success) {
                toast.success('Settings saved successfully!');
                // Optionally reload the page to apply changes
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                toast.error(response.data.error || 'Failed to save settings');
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleBusinessTypeChange = (type) => {
        const newSettings = { ...settings, businessType: type };

        // Auto-enable partners for partnership/company
        if (type === 'partnership' || type === 'company') {
            newSettings.features.enablePartners = true;
        }

        // Reset to standard mode if single owner chooses advanced
        if (type === 'single' && settings.accountingMode === 'advanced') {
            newSettings.accountingMode = 'standard';
        }

        setSettings(newSettings);
    };

    const handleAccountingModeChange = (mode) => {
        const newSettings = { ...settings, accountingMode: mode };

        // Auto-enable features based on mode
        if (mode === 'standard') {
            newSettings.features.enableCashFlow = true;
            newSettings.features.enableInventory = true;
        } else if (mode === 'advanced') {
            newSettings.features.enableCashFlow = true;
            newSettings.features.enableInventory = true;
            if (settings.businessType !== 'single') {
                newSettings.features.enablePartners = true;
            }
        }

        setSettings(newSettings);
    };

    const handleFeatureToggle = (feature) => {
        setSettings({
            ...settings,
            features: {
                ...settings.features,
                [feature]: !settings.features[feature],
            },
        });
    };

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <ReactLoading type="cylon" color="#008060" height={60} width={60} />
                <p className={styles.loadingText}>Loading settings...</p>
            </div>
        );
    }

    return (
        <div className={styles.settingsContainer}>
            <ToastContainer />

            {/* Header */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h1 className={styles.pageTitle}>Business Settings</h1>
                    <p className={styles.pageSubtitle}>
                        Configure your business type and accounting preferences
                    </p>
                </div>
            </div>

            {/* Business Type Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <AiOutlineShop className={styles.sectionIcon} />
                    <h2 className={styles.sectionTitle}>Business Type</h2>
                </div>
                <p className={styles.sectionDescription}>
                    Choose the type that best describes your business structure
                </p>

                <div className={styles.businessTypeGrid}>
                    {/* Single Owner */}
                    <div
                        className={`${styles.businessTypeCard} ${
                            settings.businessType === 'single' ? styles.selected : ''
                        }`}
                        onClick={() => handleBusinessTypeChange('single')}
                    >
                        <div className={styles.cardIcon}>
                            <AiOutlineShop />
                        </div>
                        <h3 className={styles.cardTitle}>Single Owner</h3>
                        <p className={styles.cardDescription}>
                            For solo entrepreneurs and small shops. Simple tracking without
                            partners.
                        </p>
                        {settings.businessType === 'single' && (
                            <div className={styles.selectedBadge}>
                                <AiOutlineCheckCircle /> Selected
                            </div>
                        )}
                    </div>

                    {/* Partnership */}
                    <div
                        className={`${styles.businessTypeCard} ${
                            settings.businessType === 'partnership' ? styles.selected : ''
                        }`}
                        onClick={() => handleBusinessTypeChange('partnership')}
                    >
                        <div className={styles.cardIcon}>
                            <AiOutlineTeam />
                        </div>
                        <h3 className={styles.cardTitle}>Partnership</h3>
                        <p className={styles.cardDescription}>
                            For businesses with 2+ partners. Track capital, drawings, and
                            profit sharing.
                        </p>
                        {settings.businessType === 'partnership' && (
                            <div className={styles.selectedBadge}>
                                <AiOutlineCheckCircle /> Selected
                            </div>
                        )}
                    </div>

                    {/* Company */}
                    <div
                        className={`${styles.businessTypeCard} ${
                            settings.businessType === 'company' ? styles.selected : ''
                        }`}
                        onClick={() => handleBusinessTypeChange('company')}
                    >
                        <div className={styles.cardIcon}>
                            <AiOutlineBank />
                        </div>
                        <h3 className={styles.cardTitle}>Company</h3>
                        <p className={styles.cardDescription}>
                            For registered companies with shareholders. Advanced accounting
                            and reporting.
                        </p>
                        {settings.businessType === 'company' && (
                            <div className={styles.selectedBadge}>
                                <AiOutlineCheckCircle /> Selected
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Accounting Mode Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <AiOutlineDollar className={styles.sectionIcon} />
                    <h2 className={styles.sectionTitle}>Accounting Mode</h2>
                </div>
                <p className={styles.sectionDescription}>
                    Select the complexity level that matches your needs
                </p>

                <div className={styles.accountingModeGrid}>
                    {/* Simple Mode */}
                    <div
                        className={`${styles.modeCard} ${
                            settings.accountingMode === 'simple' ? styles.modeSelected : ''
                        }`}
                        onClick={() => handleAccountingModeChange('simple')}
                    >
                        <div className={styles.modeHeader}>
                            <h3 className={styles.modeTitle}>Simple</h3>
                            {settings.accountingMode === 'simple' && (
                                <AiOutlineCheckCircle className={styles.modeCheck} />
                            )}
                        </div>
                        <ul className={styles.modeFeatures}>
                            <li>Basic expense tracking</li>
                            <li>Purchase recording</li>
                            <li>Simple reports</li>
                            <li>Minimal setup</li>
                        </ul>
                    </div>

                    {/* Standard Mode */}
                    <div
                        className={`${styles.modeCard} ${
                            settings.accountingMode === 'standard' ? styles.modeSelected : ''
                        }`}
                        onClick={() => handleAccountingModeChange('standard')}
                    >
                        <div className={styles.modeHeader}>
                            <h3 className={styles.modeTitle}>Standard</h3>
                            {settings.accountingMode === 'standard' && (
                                <AiOutlineCheckCircle className={styles.modeCheck} />
                            )}
                        </div>
                        <ul className={styles.modeFeatures}>
                            <li>Cash flow tracking</li>
                            <li>Inventory management</li>
                            <li>Payment tracking</li>
                            <li>Balance sheet</li>
                        </ul>
                    </div>

                    {/* Advanced Mode */}
                    <div
                        className={`${styles.modeCard} ${
                            settings.accountingMode === 'advanced' ? styles.modeSelected : ''
                        } ${settings.businessType === 'single' ? styles.modeDisabled : ''}`}
                        onClick={() => {
                            if (settings.businessType !== 'single') {
                                handleAccountingModeChange('advanced');
                            }
                        }}
                    >
                        <div className={styles.modeHeader}>
                            <h3 className={styles.modeTitle}>Advanced</h3>
                            {settings.accountingMode === 'advanced' && (
                                <AiOutlineCheckCircle className={styles.modeCheck} />
                            )}
                        </div>
                        <ul className={styles.modeFeatures}>
                            <li>Partner management</li>
                            <li>Profit distribution</li>
                            <li>Double-entry bookkeeping</li>
                            <li>Advanced reports</li>
                        </ul>
                        {settings.businessType === 'single' && (
                            <div className={styles.modeNote}>
                                Requires Partnership or Company
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Optional Features Section */}
            <div className={styles.section}>
                <div className={styles.sectionHeader}>
                    <AiOutlineSetting className={styles.sectionIcon} />
                    <h2 className={styles.sectionTitle}>Optional Features</h2>
                </div>
                <p className={styles.sectionDescription}>
                    Enable or disable specific features based on your needs
                </p>

                <div className={styles.featuresGrid}>
                    {/* Cash Flow */}
                    <div className={styles.featureCard}>
                        <div className={styles.featureHeader}>
                            <div className={styles.featureInfo}>
                                <h3 className={styles.featureTitle}>Cash Flow Tracking</h3>
                                <p className={styles.featureDescription}>
                                    Track money in and out, real-time cash balance
                                </p>
                            </div>
                            <label className={styles.toggleSwitch}>
                                <input
                                    type="checkbox"
                                    checked={settings.features.enableCashFlow}
                                    onChange={() => handleFeatureToggle('enableCashFlow')}
                                />
                                <span className={styles.toggleSlider}></span>
                            </label>
                        </div>
                    </div>

                    {/* Inventory */}
                    <div className={styles.featureCard}>
                        <div className={styles.featureHeader}>
                            <div className={styles.featureInfo}>
                                <h3 className={styles.featureTitle}>Inventory Management</h3>
                                <p className={styles.featureDescription}>
                                    Auto-sync inventory from purchases, track stock levels
                                </p>
                            </div>
                            <label className={styles.toggleSwitch}>
                                <input
                                    type="checkbox"
                                    checked={settings.features.enableInventory}
                                    onChange={() => handleFeatureToggle('enableInventory')}
                                />
                                <span className={styles.toggleSlider}></span>
                            </label>
                        </div>
                    </div>

                    {/* Partners */}
                    <div className={styles.featureCard}>
                        <div className={styles.featureHeader}>
                            <div className={styles.featureInfo}>
                                <h3 className={styles.featureTitle}>Partner Management</h3>
                                <p className={styles.featureDescription}>
                                    Track multiple owners, capital accounts, profit sharing
                                </p>
                            </div>
                            <label className={styles.toggleSwitch}>
                                <input
                                    type="checkbox"
                                    checked={settings.features.enablePartners}
                                    onChange={() => handleFeatureToggle('enablePartners')}
                                    disabled={
                                        settings.businessType === 'partnership' ||
                                        settings.businessType === 'company'
                                    }
                                />
                                <span className={styles.toggleSlider}></span>
                            </label>
                        </div>
                        {(settings.businessType === 'partnership' ||
                            settings.businessType === 'company') && (
                            <div className={styles.featureNote}>
                                <AiOutlineInfoCircle /> Required for Partnership/Company
                            </div>
                        )}
                    </div>

                    {/* Approval */}
                    <div className={styles.featureCard}>
                        <div className={styles.featureHeader}>
                            <div className={styles.featureInfo}>
                                <h3 className={styles.featureTitle}>Approval Workflows</h3>
                                <p className={styles.featureDescription}>
                                    Require approval for large expenses and purchases
                                </p>
                            </div>
                            <label className={styles.toggleSwitch}>
                                <input
                                    type="checkbox"
                                    checked={settings.features.enableApproval}
                                    onChange={() => handleFeatureToggle('enableApproval')}
                                />
                                <span className={styles.toggleSlider}></span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>

            {/* Save Button */}
            <div className={styles.footer}>
                <button
                    className={styles.saveButton}
                    onClick={handleSaveSettings}
                    disabled={saving}
                >
                    {saving ? (
                        <>
                            <ReactLoading type="spin" color="#fff" height={20} width={20} />
                            Saving...
                        </>
                    ) : (
                        <>
                            <AiOutlineCheckCircle />
                            Save Settings
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

export default BusinessSettings;
